import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /** Quieter action beside the primary one — "Learn how", "Import instead". */
  secondaryAction?: ReactNode;
  /** `plain` drops the dashed frame for use inside an existing card. */
  variant?: 'framed' | 'plain';
  className?: string;
}

/**
 * Empty states are designed, never blank: an icon, one honest line, and the next
 * action. Used by every list surface in the app.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  variant = 'framed',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 py-14 text-center',
        variant === 'framed' && 'rounded-xl border border-dashed border-border bg-surface/40',
        className,
      )}
    >
      {icon && (
        <div
          className={cn(
            'mb-4 flex size-11 items-center justify-center rounded-full',
            'bg-elevated text-muted ring-1 ring-border [&_svg]:size-5',
          )}
          aria-hidden
        >
          {icon}
        </div>
      )}
      <h3 className="text-sm font-semibold text-fg">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-md text-sm leading-relaxed text-muted">{description}</p>
      )}
      {(action || secondaryAction) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
